/**
 * Unmatched and recently matched inbound email documents (PF-136).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import PreviewIcon from '@mui/icons-material/Visibility';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import {
  inboundDocumentSender,
  useAssignInboundDocument,
  useInboundDocuments,
  useRejectInboundDocument,
  type InboundDocument,
} from '@/hooks/useInboundDocuments';
import { useNotify } from '@/hooks/useNotify';
import { useOpenWorkOrder, useViewedWorkOrderId } from '@/hooks/useWorkOrderWindow';
import { useAuthStore } from '@/stores/authStore';
import { formatDateTime } from '@/utils/locale';
import { AssignInboundDocumentDialog } from './AssignInboundDocumentDialog';
import { InboundDocumentPreviewDialog } from './InboundDocumentPreviewDialog';

const MATCH_REASON_KEYS: Record<string, string> = {
  no_identifier: 'inbox.reason.noIdentifier',
  no_active_work_order: 'inbox.reason.noActiveWorkOrder',
  ambiguous: 'inbox.reason.ambiguous',
  unknown_sender: 'inbox.reason.unknownSender',
};

type InboxTab = 'unmatched' | 'matched';

export default function InboxDocumentsPage() {
  const openWorkOrder = useOpenWorkOrder();
  const viewedWorkOrderId = useViewedWorkOrderId();
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canUpdate = hasPermission('work_orders.update');

  const [tab, setTab] = useState<InboxTab>('unmatched');
  const [preview, setPreview] = useState<InboundDocument | null>(null);
  const [assigning, setAssigning] = useState<InboundDocument | null>(null);
  const [rejecting, setRejecting] = useState<InboundDocument | null>(null);

  const unmatchedQuery = useInboundDocuments('unmatched');
  const matchedQuery = useInboundDocuments('matched');
  const assignMutation = useAssignInboundDocument();
  const rejectMutation = useRejectInboundDocument();

  const activeQuery = tab === 'unmatched' ? unmatchedQuery : matchedQuery;
  const documents = activeQuery.data?.documents ?? [];

  const handleAssign = async (bookingId: string) => {
    if (!assigning) return;
    try {
      await assignMutation.mutateAsync({ documentId: assigning.id, bookingId });
      notify.success(t('inbox.assigned'), t('inbox.assignedDesc'));
      setAssigning(null);
    } catch {
      notify.error(tc('error'), t('inbox.assignFailed'));
    }
  };

  const handleReject = async () => {
    if (!rejecting) return;
    try {
      await rejectMutation.mutateAsync(rejecting.id);
      notify.success(t('inbox.rejected'), t('inbox.rejectedDesc'));
      setRejecting(null);
    } catch {
      notify.error(tc('error'), t('inbox.rejectFailed'));
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>
          {t('inbox.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('inbox.subtitle')}
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_event, value: InboxTab) => setTab(value)}
        sx={{ mb: 2 }}
      >
        <Tab value="unmatched" label={t('inbox.unmatchedTab')} />
        <Tab value="matched" label={t('inbox.matchedTab')} />
      </Tabs>

      {activeQuery.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {activeQuery.isError && (
        <Alert severity="error">{t('inbox.loadFailed')}</Alert>
      )}

      {!activeQuery.isLoading && !activeQuery.isError && documents.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          {tab === 'unmatched' ? t('inbox.emptyUnmatched') : t('inbox.emptyMatched')}
        </Typography>
      )}

      {!activeQuery.isLoading && documents.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('inbox.receivedAt')}</TableCell>
              <TableCell>{t('inbox.sender')}</TableCell>
              <TableCell>{t('inbox.subject')}</TableCell>
              <TableCell>{t('inbox.attachments')}</TableCell>
              {tab === 'unmatched' && <TableCell>{t('inbox.reasonLabel')}</TableCell>}
              {tab === 'matched' && <TableCell>{t('inbox.matchedWorkOrder')}</TableCell>}
              <TableCell align="right">{tc('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.map((row) => (
              <TableRow
                key={row.id}
                hover
                selected={viewedWorkOrderId !== null && row.matchedBookingId === viewedWorkOrderId}
              >
                <TableCell>{formatDateTime(row.receivedAt)}</TableCell>
                <TableCell>{inboundDocumentSender(row)}</TableCell>
                <TableCell>{row.subject ?? t('inbox.noSubject')}</TableCell>
                <TableCell>
                  {t('attachments.count', { count: row.attachmentIds.length })}
                </TableCell>
                {tab === 'unmatched' && (
                  <TableCell>
                    {row.matchReason ? (
                      <Chip
                        size="small"
                        label={t(MATCH_REASON_KEYS[row.matchReason] ?? 'inbox.reason.unknown')}
                      />
                    ) : null}
                  </TableCell>
                )}
                {tab === 'matched' && (
                  <TableCell>
                    {row.matchedBookingId ? (
                      <Link
                        component={RouterLink}
                        to={`/workorders/${row.matchedBookingId}`}
                        onClick={(e: React.MouseEvent) => {
                          // Modified clicks keep the link's native new-tab behaviour.
                          if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
                          e.preventDefault();
                          openWorkOrder(row.matchedBookingId!);
                        }}
                      >
                        {t('inbox.openWorkOrder')}
                      </Link>
                    ) : null}
                  </TableCell>
                )}
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                    <Tooltip title={t('inbox.preview')}>
                      <IconButton
                        size="small"
                        aria-label={t('inbox.preview')}
                        onClick={() => setPreview(row)}
                      >
                        <PreviewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {tab === 'unmatched' && canUpdate && (
                      <>
                        <Button size="small" onClick={() => setAssigning(row)}>
                          {t('inbox.assign')}
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => setRejecting(row)}
                        >
                          {t('inbox.reject')}
                        </Button>
                      </>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <InboundDocumentPreviewDialog
        open={preview !== null}
        document={preview}
        onClose={() => setPreview(null)}
      />

      <AssignInboundDocumentDialog
        open={assigning !== null}
        document={assigning}
        loading={assignMutation.isPending}
        onClose={() => setAssigning(null)}
        onAssign={(bookingId) => {
          void handleAssign(bookingId);
        }}
      />

      <ConfirmDialog
        open={rejecting !== null}
        title={t('inbox.reject')}
        message={t('inbox.rejectConfirm')}
        confirmColor="error"
        loading={rejectMutation.isPending}
        onConfirm={() => {
          void handleReject();
        }}
        onCancel={() => setRejecting(null)}
      />
    </Box>
  );
}
